import "driver.js/dist/driver.css";

import { driver } from "driver.js";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

interface TourStepConfig {
  /** Matches the data-tour="..." attribute on the target element. Omit for a centered (no-highlight) step. */
  dataKey?: string;
  titleKey: string;
  /** Translation key for plain-text description. Required if htmlDescFn is not provided. */
  descKey?: string;
  /** Function returning an HTML string for rich descriptions. Receives the t() function. */
  htmlDescFn?: (t: (key: string) => string) => string;
  popoverSide?: "top" | "bottom" | "left" | "right";
  /** Limit step visibility to a specific viewport. Defaults to 'all'. */
  visibleOn?: "all" | "desktop" | "mobile";
}

function makeWordTranslationHTML(t: (key: string) => string): string {
  return `
    <style>
      @keyframes iriz-word-select{0%,15%{background:transparent}25%,70%{background:#bfdbfe;border-radius:2px}80%,100%{background:transparent}}
      @keyframes iriz-tooltip-show{0%,25%{opacity:0}35%,65%{opacity:1}75%,100%{opacity:0}}
    </style>
    <div style="font-size:13px;line-height:1.7;margin-bottom:16px">${t("pages:voicechatTour.wordTranslationDescription")}</div>
    <div style="display:flex;justify-content:center;padding:28px 0 12px">
      <p style="font-size:14px;line-height:1.6">The quick&nbsp;<span style="position:relative;display:inline-block"><span style="animation:iriz-word-select 3.5s ease-in-out infinite;padding:0 3px">brown</span><span style="position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%);animation:iriz-tooltip-show 3.5s ease-in-out infinite;background:#0d9488;color:white;border-radius:8px;padding:4px 10px;font-size:11px;white-space:nowrap;pointer-events:none;display:block">marr&#243;n<span style="position:absolute;top:100%;left:50%;transform:translateX(-50%) translateY(-50%) rotate(45deg);width:8px;height:8px;background:#0d9488;display:block"></span></span></span>&nbsp;fox jumps</p>
    </div>
  `.trim();
}

function makeHighlightsHTML(t: (key: string) => string): string {
  return `
    <div style="font-size:13px;line-height:1.7;margin-bottom:16px">${t("pages:voicechatTour.highlightsDescription")}</div>
    <div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap">
      <span style="background:#a7f3d0;color:#065f46;border-radius:9999px;padding:2px 10px;font-size:12px;font-weight:500">${t("common:highlights.suggestion")}</span>
      <span style="background:#fef08a;color:#713f12;border-radius:9999px;padding:2px 10px;font-size:12px;font-weight:500">${t("common:highlights.pronunciation")}</span>
    </div>
    <p style="font-size:13px;line-height:1.8">You did well overall, but try using&nbsp;<mark style="background:#a7f3d0;color:#065f46;padding:1px 3px;border-radius:3px">a more natural phrase</mark>&nbsp;here. Also, watch the pronunciation of&nbsp;<mark style="background:#fef08a;color:#713f12;padding:1px 3px;border-radius:3px">this expression</mark>.</p>
  `.trim();
}

/**
 * Ordered list of tour steps for the VoiceChatPage.
 *
 * To add, remove, or reorder steps: edit this array.
 * To change which element a step highlights: update the `dataKey` value and
 * the corresponding `data-tour="<dataKey>"` attribute in the component.
 * Steps without a `dataKey` appear centered on screen with no element highlight.
 */
const TOUR_STEPS: TourStepConfig[] = [
  {
    dataKey: "mode-carousel",
    titleKey: "pages:voicechatTour.modeCarouselTitle",
    descKey: "pages:voicechatTour.modeCarouselDescription",
    popoverSide: "bottom",
  },
  {
    dataKey: "mode-context",
    titleKey: "pages:voicechatTour.modeContextTitle",
    descKey: "pages:voicechatTour.modeContextDescription",
    popoverSide: "right",
  },
  {
    dataKey: "mode-side-panel",
    titleKey: "pages:voicechatTour.sidePanelTitle",
    descKey: "pages:voicechatTour.sidePanelDescription",
    popoverSide: "left",
  },
  {
    dataKey: "transcript-area",
    titleKey: "pages:voicechatTour.transcriptTitle",
    descKey: "pages:voicechatTour.transcriptDescription",
    popoverSide: "top",
  },
  {
    dataKey: "mic-button",
    titleKey: "pages:voicechatTour.micButtonTitle",
    descKey: "pages:voicechatTour.micButtonDescription",
    popoverSide: "top",
  },
  {
    dataKey: "translation-toggle",
    titleKey: "pages:voicechatTour.translationPanelTitle",
    descKey: "pages:voicechatTour.translationPanelDescription",
    popoverSide: "left",
  },
  {
    dataKey: "send-comment-or-bug",
    titleKey: "pages:voicechatTour.sendCommentOrBugTitle",
    descKey: "pages:voicechatTour.sendCommentOrBugDescription",
    popoverSide: "right",
  },
  {
    titleKey: "pages:voicechatTour.wordTranslationTitle",
    htmlDescFn: makeWordTranslationHTML,
  },
  {
    titleKey: "pages:voicechatTour.highlightsTitle",
    htmlDescFn: makeHighlightsHTML,
  },
];

const DESKTOP_BREAKPOINT = "(min-width: 768px)";

export function useVoiceChatTour() {
  const { t } = useTranslation(["pages"]);

  const startTour = useCallback(
    (onComplete?: () => void) => {
      const isDesktop = window.matchMedia(DESKTOP_BREAKPOINT).matches;

      const steps = TOUR_STEPS
        // Filter by viewport visibility
        .filter((step) => {
          if (step.visibleOn === "desktop") return isDesktop;
          if (step.visibleOn === "mobile") return !isDesktop;
          return true;
        })
        // Map to driver.js step format
        .map((step) => ({
          ...(step.dataKey ? { element: `[data-tour="${step.dataKey}"]` } : {}),
          popover: {
            title: t(step.titleKey),
            description: step.htmlDescFn
              ? step.htmlDescFn(t)
              : t(step.descKey ?? ""),
            side: step.popoverSide,
          },
        }))
        // Skip steps whose target element is not currently in the DOM
        .filter(
          (step) =>
            !("element" in step) ||
            document.querySelector(step.element as string) !== null,
        );

      const driverObj = driver({
        showProgress: true,
        animate: true,
        steps,
        nextBtnText: t("pages:voicechatTour.nextButton"),
        prevBtnText: t("pages:voicechatTour.prevButton"),
        doneBtnText: t("pages:voicechatTour.doneButton"),
        progressText: t("pages:voicechatTour.progressText"),
        onDestroyed: onComplete,
      });

      driverObj.drive();
    },
    [t],
  );

  return { startTour };
}
